import { useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardDescription } from "@/components/ui/card"
import { useAuth } from "@/contexts/AuthContext"


export default function MfaVerification() {
  const [mfaCode, setMfaCode] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")

  const { verifyMfa } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Get MFA data from navigation state
  const mfaData = location.state?.mfaData
  // Preserve the deep link the user originally opened (e.g. a share URL) —
  // hard-navigating to "/" here used to drop it and land them on the dashboard.
  const redirectTo = location.state?.from?.pathname
    ? `${location.state.from.pathname}${location.state.from.search || ""}`
    : "/"

  // Redirect if no MFA data
  if (!mfaData) {
    navigate("/", { replace: true })
    return null
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError("")

    try {
      const result = await verifyMfa({
        email: mfaData.email,
        mfa_code: mfaCode,
        session: mfaData.session
      })

      if (result.success) {
        // MFA verification successful - user will be redirected by the ProtectedRoute
        console.log('MFA verification successful')
        navigate(redirectTo, { replace: true })
      } else {
        setError(result.error || "Invalid MFA code. Please try again.")
      }
    } catch (error) {
      setError(error?.message || "An unexpected error occurred. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    // TODO: Implement resend code functionality
    console.log("Resend code clicked")
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <Card className="shadow-lg">
          <CardHeader className="space-y-6 pb-8">
            {/* Logo Section */}
            <div className="flex items-center justify-center">
              <div className="text-4xl font-bold text-gray-900">
                Mindshine Data Governance
              </div>
            </div>
            <CardDescription className="text-center text-base">
              Enter the verification code sent to <strong>{mfaData.otp_recipient}</strong> via {mfaData.otp_channel}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                  {error}
                </div>
              )}

              {/* MFA Code Field */}
              <div className="space-y-2">
                <Label htmlFor="mfaCode" className="text-base font-medium text-gray-700">
                  Verification Code
                </Label>
                <Input
                  id="mfaCode"
                  type="text"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  className="h-12 text-base border-gray-300 rounded-md text-center tracking-widest"
                  maxLength={6}
                  required
                  disabled={isLoading}
                  autoFocus
                />
              </div>

              {/* Verify Button */}
              <Button
                type="submit"
                disabled={isLoading || mfaCode.length !== 6}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium text-base rounded-md"
              >
                {isLoading ? "Verifying..." : "Verify"}
              </Button>
            </form>

            {/* Resend Code Link */}
            <div className="text-center">
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700 underline text-base"
                onClick={handleResendCode}
                disabled={isLoading}
              >
                Resend verification code
              </button>
            </div>

            {/* Back to Login Link */}
            <div className="text-center">
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700 underline text-base"
                onClick={() => navigate("/", { replace: true })}
                disabled={isLoading}
              >
                Back to login
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
